import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { StockShareType } from '../types/on-boarding.type';
import CustomSelect from 'components/Form/CustomSelect';
import {
  useCreateStock,
  useDeleteStock,
  useGetStockList
} from 'common/queries-and-mutations/stock-share';
import { Loader } from 'components/Loader';
import { findNextPage, findPreviousPage, showRemoveButton } from 'utils';
import { countriesCurrency } from 'constants/countries-currency';
import InfoBar from './InfoBar';
import { STEPS } from 'constants/steps';
import { fetchUserInformation } from 'thunks/account-thunk';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import { useSkipOnboardingStep } from 'common/queries-and-mutations/user';

export default function StockShareInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isError, isLoading } = useCreateStock();
  const { mutate: deleteStock, isLoading: isDeleting } = useDeleteStock();
  const { mutate: skipOnboarding, isSuccess: skipSuccess } =
    useSkipOnboardingStep();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);
  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const {
    data,
    isSuccess: isStockSuccess,
    isLoading: isStockLoading
  } = useGetStockList();

  const stocks = useMemo(() => {
    if (isStockSuccess && data.length > 0) {
      return data.map(stock => ({
        stockId: stock.id,
        companyName: stock.companyName,
        certificateNo: stock.certificateNo,
        registerName: stock.registerName,
        registerAddress: stock.registerAddress,
        country: stock.country
      }));
    } else {
      return [
        {
          stockId: null,
          companyName: '',
          certificateNo: '',
          registerName: '',
          registerAddress: '',
          country: ''
        }
      ];
    }
  }, [data, isStockSuccess]);

  const formik = useFormik({
    initialValues: {
      stocks
    },
    validationSchema: Yup.object({
      stocks: Yup.array().of(
        Yup.object().shape({
          companyName: Yup.string().required('Tell us the name of the company'),
          certificateNo: Yup.string().required(
            'C.S.C.N / Cerification No. is required'
          ),
          registerName: Yup.string()
            .required(`Registrar's name is required`)
            .nullable(),
          registerAddress: Yup.string()
            .required(`Registrar's address is required`)
            .nullable(),
          country: Yup.string().required('Please select country')
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onStockSubmit(values);
    }
  });

  const addMoreStock = () => {
    formik.values.stocks.push({
      stockId: null,
      companyName: '',
      certificateNo: '',
      registerName: '',
      registerAddress: '',
      country: ''
    });
    formik.setFieldValue('stocks', [...formik.values.stocks]);
  };

  const removeStock = (indexToRemove: number, stockId?: number | null) => {
    if (stockId) {
      deleteStock(stockId);
    }
    formik.values.stocks.splice(indexToRemove, 1);
    formik.setFieldValue('stocks', [...formik.values.stocks]);
  };

  const onStockSubmit = (formValue: StockShareType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.STOCK_INFORMATION));
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, dispatch, navigate, planAllowPage]);

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return;
    }
    mutate(formik.values);
  };

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.STOCK_INFORMATION));
  };

  const handleSkip = () => {
    setIsSubmitting(true);
    skipOnboarding();
  };

  useEffect(() => {
    if (skipSuccess) {
      dispatch(fetchUserInformation());
      navigate('/dashboard');
    }
  }, [skipSuccess, dispatch]);

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={2}
        stepPercentage={STEPS.STOCK_INFORMATION}
        timelineStep={2}
      />
      {isStockLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <div className="flex w-full items-end justify-between">
            <span></span>
            <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
              Financial Asset - Stocks/Shares Information
            </p>
            <button
              type="button"
              className="mt-5 text-sm font-normal text-[#353535] underline"
              onClick={handleSkip}
            >
              Skip to Dashboard
            </button>
          </div>

          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            {formik.values.stocks.map((stock, index) => (
              <div key={index} className="my-8">
                <div className="grid md:grid-cols-2 md:gap-9">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`stocks[${index}].companyName`}
                      placeholder=""
                      value={stock.companyName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Name of Company"
                      error={
                        formik.touched.stocks
                          ? formik.errors.stocks?.[index] &&
                            formik.errors.stocks.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.stocks[index])
                              ).companyName
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`stocks[${index}].certificateNo`}
                      placeholder=""
                      value={stock.certificateNo}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="C.S.C.S No./Cerificate No."
                      error={
                        formik.touched.stocks
                          ? formik.errors.stocks?.[index] &&
                            formik.errors.stocks.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.stocks[index])
                              ).certificateNo
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-9">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`stocks[${index}].registerName`}
                      placeholder=""
                      value={stock.registerName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label={`Registrar's Name`}
                      error={
                        formik.touched.stocks
                          ? formik.errors.stocks?.[index] &&
                            formik.errors.stocks.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.stocks[index])
                              ).registerName
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                      isRequired
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`stocks[${index}].registerAddress`}
                      placeholder=""
                      value={stock.registerAddress}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label={`Registrar's Address`}
                      error={
                        formik.touched.stocks
                          ? formik.errors.stocks?.[index] &&
                            formik.errors.stocks.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.stocks[index])
                              ).registerAddress
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                      isRequired
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-9">
                  <div className="group relative z-0 mb-6 w-full">
                    <CustomSelect
                      options={countriesCurrency.map(country => ({
                        value: country.name,
                        label: country.name
                      }))}
                      selectedOption={{
                        value: stock.country,
                        label: stock.country
                      }}
                      handleOptionChange={option => {
                        formik.setFieldValue(
                          `stocks[${index}].country`,
                          option?.value
                        );
                      }}
                      label="Country"
                      name="country"
                      isRequired
                      isSearchable
                      className="h-[3.5rem] rounded-2xl"
                      error={
                        formik.touched.stocks
                          ? formik.errors.stocks?.[index] &&
                            formik.errors.stocks.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.stocks[index])
                              ).country
                            : ''
                          : ''
                      }
                    />
                  </div>
                </div>
                {showRemoveButton(index, stock.stockId, data!.length > 1) && (
                  <div className="group relative z-0 mt-1 w-full">
                    {isDeleting ? (
                      <Loader height={20} width={20} type={'Wait'} />
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeStock(index, stock.stockId)}
                      >
                        <Icon name={Icons.Delete} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="group z-0 flex w-full items-center justify-end">
              <div
                className="flex items-center gap-4"
                onClick={addMoreStock}
                role="button"
                tabIndex={-1}
              >
                <Icon name={Icons.AddCircleOutline} />
                <span className="mt-2 text-base font-medium text-[#353535]">
                  Add Another Stock/Shares
                </span>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Icon name={Icons.Required} />
                <span className="text-[10px] text-[#7A7C87]">
                  Indicates Required
                </span>
              </div>
            </div>

            <div className="flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-5 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-10 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={() => {
                      onContinueLater();
                      setIsLater(true);
                    }}
                  >
                    Save and Continue Later
                  </button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Proceed"
                  type="submit"
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
